import React, { Component } from "react";
import classNames from "classnames";
import { FormField } from "react-form";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import FormHelperText from "@material-ui/core/FormHelperText";
import InputLabel from "@material-ui/core/InputLabel";
import { withStyles } from "@material-ui/core";

const styles = (theme) => ({
    //font comes from the MuiInputLabel theme overrides so it matches the other inputs
    label: {
        marginBottom: 4,
    },
    editor: {
        backgroundColor: "#fff",
        borderRadius: 4,
        "& .ql-toolbar": {
            borderColor: "#979797",
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
        },
        "& .ql-container": {
            borderColor: "#979797",
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
            fontFamily: "inherit",
            fontSize: "15px",
            color: "#262626",
        },
        //larger than the standard inputs so the field stands out on the form
        "& .ql-editor": {
            minHeight: 200,
            maxHeight: 500,
            overflowY: "auto",
        },
    },
    editorError: {
        "& .ql-toolbar, & .ql-container": {
            borderColor: "#f44336",
        },
    },
    disabled: {
        backgroundColor: "#f5f5f5",
        "& .ql-toolbar": {
            display: "none",
        },
        "& .ql-container": {
            borderTop: "1px solid #979797 !important",
            borderRadius: 4,
            color: "rgba(0, 0, 0, 0.65)",
        },
        "& .ql-editor": {
            minHeight: 100,
        },
    },
});

const toolbar = [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    [{ align: [] }],
    ["link", "blockquote"],
    ["clean"],
];

//returns the plain text of a Text Area (html) value, used where the value is displayed outside of the editor (tables, etc.)
export function stripHtml(html) {
    if (!html) return "";
    return new DOMParser().parseFromString(html, "text/html").body.textContent || "";
}

// See https://react-form.js.org/#/custom-input
class RichTextFieldWrapper extends Component {
    componentDidMount() {
        const { disabled, placeholder } = this.props;

        this.quill = new Quill(this.editorRef, {
            theme: "snow",
            modules: { toolbar },
            readOnly: disabled,
            placeholder,
        });
        this.setContents(this.props.fieldApi.getValue());
        this.quill.on("text-change", this.handleChange);
        //blur fires on mousedown, so the value is pushed to the form before a save button's click runs
        this.quill.root.addEventListener("blur", this.flush);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.disabled !== this.props.disabled) this.quill.enable(!this.props.disabled);

        //value was changed outside of the editor (form reset, switching records, etc.)
        const value = this.props.fieldApi.getValue();
        if (value !== this.lastValue) this.setContents(value);
    }

    componentWillUnmount() {
        clearTimeout(this.timer);
        this.quill.off("text-change", this.handleChange);
        this.quill.root.removeEventListener("blur", this.flush);
    }

    setContents = (value) => {
        this.lastValue = value;
        //silent so loading a saved value doesnt dirty the form
        this.quill.setContents(this.quill.clipboard.convert({ html: value || "" }), "silent");
    };

    //setting the form value re-renders the entire record form, so wait for a pause in typing instead of updating on every keystroke
    handleChange = (delta, oldDelta, source) => {
        if (source !== "user") return;

        clearTimeout(this.timer);
        this.timer = setTimeout(this.flush, 400);
    };

    flush = () => {
        clearTimeout(this.timer);

        const { fieldApi, eventHandle } = this.props;
        const { setValue, setTouched } = fieldApi;

        //an emptied editor still contains markup (<p><br></p>) so store null instead
        var value = this.quill.getText().trim() === "" ? null : this.quill.root.innerHTML;
        if (value === this.lastValue) return;
        this.lastValue = value;

        setValue(value);
        setTouched();

        if (eventHandle) eventHandle(value);
    };

    render() {
        const { fieldApi, classes, label, disabled, required, helperText } = this.props;
        const { getError, getWarning, getSuccess } = fieldApi;

        const error = getError(),
            hasError = !!error,
            errorText = error || getWarning() || getSuccess() || helperText;

        return (
            <>
                {label && (
                    <InputLabel shrink className={classes.label} required={required} error={hasError}>
                        {label}
                    </InputLabel>
                )}
                {/* quill inserts its toolbar before the editor div so keep the editor as this wrappers only react child */}
                <div className={classNames(classes.editor, hasError && classes.editorError, disabled && classes.disabled)}>
                    <div ref={(el) => (this.editorRef = el)} />
                </div>
                {errorText && <FormHelperText error={hasError}>{errorText}</FormHelperText>}
                {required && !hasError && <FormHelperText style={{ marginTop: -1 }}>Required</FormHelperText>}
            </>
        );
    }
}
RichTextFieldWrapper.displayName = "RichTextFieldWrapper";

const RichTextField = FormField(RichTextFieldWrapper);

export default withStyles(styles)(RichTextField);
